import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AccountTree as TreeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { chartOfAccountService } from '../../services/chartOfAccount.service';
import { useConfirm } from '../../hooks/useConfirm';
import {
  ChartOfAccount,
  AccountType,
  ACCOUNT_TYPES,
  getAccountTypeLabel,
  getAccountNatureLabel,
} from '../../types/chartOfAccount';

const ChartOfAccountsList: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const confirm = useConfirm();
  
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<AccountType | 'All'>('All');
  const [showInactive, setShowInactive] = useState(false);
  const [showOnlyFinal, setShowOnlyFinal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Translations
  const t = {
    title: isArabic ? 'دليل الحسابات' : 'Chart of Accounts',
    treeView: isArabic ? 'عرض شجري' : 'Tree View',
    newAccount: isArabic ? 'حساب جديد' : 'New Account',
    search: isArabic ? 'بحث برقم أو اسم الحساب...' : 'Search by code or name...',
    accountType: isArabic ? 'نوع الحساب' : 'Account Type',
    all: isArabic ? 'الكل' : 'All',
    status: isArabic ? 'الحالة' : 'Status',
    activeOnly: isArabic ? 'نشط فقط' : 'Active Only',
    accounts: isArabic ? 'الحسابات' : 'Accounts',
    finalOnly: isArabic ? 'نهائية فقط' : 'Final Only',
    accountCode: isArabic ? 'رقم الحساب' : 'Account Code',
    accountName: isArabic ? 'اسم الحساب' : 'Account Name',
    parentAccount: isArabic ? 'الحساب الأب' : 'Parent Account',
    type: isArabic ? 'النوع' : 'Type',
    nature: isArabic ? 'الطبيعة' : 'Nature',
    openingBalance: isArabic ? 'الرصيد الافتتاحي' : 'Opening Balance',
    currentBalance: isArabic ? 'الرصيد الحالي' : 'Current Balance',
    level: isArabic ? 'المستوى' : 'Level',
    actions: isArabic ? 'الإجراءات' : 'Actions',
    active: isArabic ? 'نشط' : 'Active',
    inactive: isArabic ? 'غير نشط' : 'Inactive',
    noAccounts: isArabic ? 'لا توجد حسابات' : 'No accounts found',
    showing: isArabic ? 'عرض' : 'Showing',
    of: isArabic ? 'من' : 'of',
    account: isArabic ? 'حساب' : 'account',
    view: isArabic ? 'عرض' : 'View',
    edit: isArabic ? 'تعديل' : 'Edit',
    delete: isArabic ? 'حذف' : 'Delete',
    refresh: isArabic ? 'تحديث' : 'Refresh',
    deleteConfirm: isArabic ? 'هل أنت متأكد من حذف الحساب' : 'Are you sure you want to delete account',
  };

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, filterType, showOnlyFinal]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await chartOfAccountService.getAll({
        includeInactive: showInactive,
        accountType: filterType !== 'All' ? filterType : undefined,
        onlyFinalAccounts: showOnlyFinal,
      });
      
      setAccounts(data);
      setFilteredAccounts(data);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
      setError(err.response?.data?.message || (isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data'));
      setAccounts([]);
      setFilteredAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAccounts(accounts);
    } else {
      const filtered = accounts.filter(
        (account) =>
          account.accountCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.accountNameAr.includes(searchTerm)
      );
      setFilteredAccounts(filtered);
    }
    setPage(0);
  }, [searchTerm, accounts]);

  const paginatedAccounts = useMemo(
    () => filteredAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredAccounts, page, rowsPerPage]
  );

  const handleDelete = async (id: number, accountName: string) => {
    const ok = await confirm({ message: `${t.deleteConfirm} "${accountName}"؟`, severity: 'error' });
    if (!ok) return;
    try {
      await chartOfAccountService.delete(id);
      loadAccounts();
    } catch (err: any) {
      alert(err.response?.data?.message || (isArabic ? 'حدث خطأ أثناء الحذف' : 'Error deleting'));
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat(isArabic ? 'ar-KW' : 'en-GB', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
    }).format(value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
          {t.title}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAccounts}
          >
            {t.refresh}
          </Button>
          <Button
            variant="outlined"
            startIcon={<TreeIcon />}
            onClick={() => navigate('/chart-of-accounts/tree')}
          >
            {t.treeView}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/chart-of-accounts/create')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            {t.newAccount}
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* Search */}
            <TextField
              placeholder={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Account Type Filter */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t.accountType}</InputLabel>
              <Select
                value={filterType}
                label={t.accountType}
                onChange={(e) => setFilterType(e.target.value as AccountType | 'All')}
              >
                <MenuItem value="All">{t.all}</MenuItem>
                {ACCOUNT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {isArabic ? type.labelAr : type.labelEn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Show Inactive */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>{t.status}</InputLabel>
              <Select
                value={showInactive ? 'all' : 'active'}
                label={t.status}
                onChange={(e) => setShowInactive(e.target.value === 'all')}
              >
                <MenuItem value="active">{t.activeOnly}</MenuItem>
                <MenuItem value="all">{t.all}</MenuItem>
              </Select>
            </FormControl>

            {/* Show Only Final */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>{t.accounts}</InputLabel>
              <Select
                value={showOnlyFinal ? 'final' : 'all'}
                label={t.accounts}
                onChange={(e) => setShowOnlyFinal(e.target.value === 'final')}
              >
                <MenuItem value="all">{t.all}</MenuItem>
                <MenuItem value="final">{t.finalOnly}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.main' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.accountCode}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.accountName}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.parentAccount}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.type}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.nature}</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.openingBalance}</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.currentBalance}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.level}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" py={4}>
                    {t.noAccounts}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedAccounts.map((account) => (
                <TableRow key={account.accountId} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {account.accountCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {isArabic ? account.accountNameAr : account.accountName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isArabic ? account.accountName : account.accountNameAr}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {account.parentAccountCode ? (
                      <Box>
                        <Typography variant="body2" color="secondary">
                          {account.parentAccountCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isArabic ? account.parentAccountName : account.parentAccountName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getAccountTypeLabel(account.accountType, language)}
                      size="small"
                      color={
                        account.accountType === 'Asset'
                          ? 'primary'
                          : account.accountType === 'Liability'
                          ? 'error'
                          : account.accountType === 'Equity'
                          ? 'warning'
                          : account.accountType === 'Revenue'
                          ? 'success'
                          : 'default'
                      }
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getAccountNatureLabel(account.accountNature, language)}
                      size="small"
                      variant="outlined"
                      color={account.accountNature === 'Debit' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(account.openingBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(account.currentBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${isArabic ? 'المستوى' : 'Level'} ${account.level}`}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={account.isActive ? t.active : t.inactive}
                      size="small"
                      color={account.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t.view}>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => navigate(`/chart-of-accounts/view/${account.accountId}`)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.edit}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/chart-of-accounts/edit/${account.accountId}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.delete}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(account.accountId, isArabic ? account.accountNameAr : account.accountName)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredAccounts.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[50, 100, 200]}
          labelRowsPerPage={isArabic ? 'عدد الصفوف:' : 'Rows per page:'}
        />
      </TableContainer>

      {/* Results Count */}
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t.showing} {filteredAccounts.length} {t.of} {accounts.length} {t.account}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChartOfAccountsList;